import React, { useState, useCallback } from "react";
import { Box, CardContent, Typography } from "@mui/material";
import ProblemDetails from "./ProblemDetails";
import ReactionsAccordion from "./ReactionsAccordion";
import DifficultiesAccordion from "./DifficultiesAccordion";
import TagsAccordion from "./TagsAccordion";
import TranslationsAccordion from "./TranslationsAccordion";
import CompaniesAccordion from "./CompaniesAccordion";
import TestsAccordion from "./TestsAccordion";
import VideosAccordion from "./VideosAccordion";
import useMetadata from "app/hooks/useMetadata";
import AddProblemSkeleton from "components/AddProblemSkeleton";

const ProblemForm = ({ problemData, setProblemData, isUpdate }) => {
  const { metadata, error } = useMetadata();
  const [expanded, setExpanded] = useState("problemDetails");
  const [tagInput, setTagInput] = useState("");

  const handleAccordionChange = useCallback(
    (panel) => (event, isExpanded) => {
      setExpanded(isExpanded ? panel : null);
    },
    []
  );

  const handleChange = useCallback(
    (e) => {
      const { name, value, type, checked } = e.target;
      setProblemData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    },
    [setProblemData]
  );

  const handleAddTag = useCallback(
    (event) => {
      if (event.key === "Enter" && tagInput.trim()) {
        setProblemData((prev) => ({
          ...prev,
          problemTags: [
            ...prev.problemTags,
            { id: Date.now(), name: tagInput.trim() },
          ],
        }));
        setTagInput("");
      }
    },
    [tagInput, setProblemData]
  );

  const handleDeleteTag = useCallback(
    (idToDelete) => {
      setProblemData((prev) => ({
        ...prev,
        problemTags: prev.problemTags.filter((tag) => tag.id !== idToDelete),
      }));
    },
    [setProblemData]
  );

  const handleAddReaction = useCallback(
    (event) => {
      event.stopPropagation();
      setProblemData((prev) => ({
        ...prev,
        problemReactions: [
          ...prev.problemReactions,
          { count: null, reaction: metadata?.reactions?.[0]?.key || "LIKE" },
        ],
      }));
    },
    [metadata, setProblemData]
  );

  const handleDeleteReaction = useCallback(
    (index) => {
      setProblemData((prev) => ({
        ...prev,
        problemReactions: prev.problemReactions.filter((_, i) => i !== index),
      }));
    },
    [setProblemData]
  );

  const handleReactionChange = useCallback(
    (index, field, value) => {
      setProblemData((prev) => ({
        ...prev,
        problemReactions: prev.problemReactions.map((reaction, i) =>
          i === index ? { ...reaction, [field]: value } : reaction
        ),
      }));
    },
    [setProblemData]
  );

  const handleDifficultyChange = useCallback(
    (index, field, value) => {
      setProblemData((prev) => ({
        ...prev,
        problemDifficulties: prev.problemDifficulties.map((diff, i) =>
          i === index ? { ...diff, [field]: value } : diff
        ),
      }));
    },
    [setProblemData]
  );

  const handleDeleteDifficulty = useCallback(
    (index) => {
      setProblemData((prev) => ({
        ...prev,
        problemDifficulties: prev.problemDifficulties.filter(
          (_, i) => i !== index
        ),
      }));
    },
    [setProblemData]
  );

  const handleAddDifficulty = useCallback(
    (event) => {
      event.stopPropagation();
      setProblemData((prev) => ({
        ...prev,
        problemDifficulties: [
          ...prev.problemDifficulties,
          {
            difficulty: metadata?.difficulties?.[0]?.key || "EASY",
            count: null,
          },
        ],
      }));
    },
    [metadata, setProblemData]
  );

  const handleAddTranslation = useCallback(
    (event) => {
      event.stopPropagation();
      setProblemData((prev) => ({
        ...prev,
        problemTranslations: [
          ...prev.problemTranslations,
          {
            langCode: metadata?.languages?.[0]?.key || "EN",
            titleTranslation: null,
            descriptionTranslation: null,
            algorithmTranslations: [
              {
                algorithmName: null,
                flowChartDetails: null,
                problemCodeSnippets: [
                  {
                    codeSnippet: null,
                    active: true,
                    programmingLanguage:
                      metadata?.programmingLanguages?.[0]?.key,
                    description: null,
                  },
                ],
              },
            ],
          },
        ],
      }));
    },
    [metadata, setProblemData]
  );

  const handleTranslationChange = useCallback(
    (index, field, value) => {
      setProblemData((prev) => {
        if (field === "reorder") {
          return {
            ...prev,
            problemTranslations: value,
          };
        }
        return {
          ...prev,
          problemTranslations: prev.problemTranslations.map((translation, i) =>
            i === index ? { ...translation, [field]: value } : translation
          ),
        };
      });
    },
    [setProblemData]
  );

  const handleDeleteTranslation = useCallback(
    (index) => {
      setProblemData((prev) => ({
        ...prev,
        problemTranslations: prev.problemTranslations.filter(
          (_, i) => i !== index
        ),
      }));
    },
    [setProblemData]
  );

  const handleAddAlgorithmTranslation = useCallback(
    (translationIndex) => {
      setProblemData((prev) => ({
        ...prev,
        problemTranslations: prev.problemTranslations.map((translation, i) =>
          i === translationIndex
            ? {
                ...translation,
                algorithmTranslations: [
                  ...translation.algorithmTranslations,
                  {
                    algorithmName: null,
                    flowChartDetails: null,
                    problemCodeSnippets: [
                      {
                        codeSnippet: null,
                        active: true,
                        programmingLanguage:
                          metadata?.programmingLanguages?.[0]?.key,
                        description: null,
                      },
                    ],
                  },
                ],
              }
            : translation
        ),
      }));
    },
    [metadata, setProblemData]
  );

  const handleAlgorithmTranslationChange = useCallback(
    (translationIndex, algoIndex, field, value) => {
      setProblemData((prev) => ({
        ...prev,
        problemTranslations: prev.problemTranslations.map((translation, i) =>
          i === translationIndex
            ? {
                ...translation,
                algorithmTranslations:
                  field === "reorder"
                    ? value
                    : translation.algorithmTranslations.map((algo, j) =>
                        j === algoIndex ? { ...algo, [field]: value } : algo
                      ),
              }
            : translation
        ),
      }));
    },
    [setProblemData]
  );

  const handleDeleteAlgorithmTranslation = useCallback(
    (translationIndex, algoIndex) => {
      setProblemData((prev) => ({
        ...prev,
        problemTranslations: prev.problemTranslations.map((translation, i) =>
          i === translationIndex
            ? {
                ...translation,
                algorithmTranslations: translation.algorithmTranslations.filter(
                  (_, j) => j !== algoIndex
                ),
              }
            : translation
        ),
      }));
    },
    [setProblemData]
  );

  const handleAddCodeSnippet = useCallback(
    (translationIndex, algoIndex) => {
      setProblemData((prev) => ({
        ...prev,
        problemTranslations: prev.problemTranslations.map((translation, i) =>
          i === translationIndex
            ? {
                ...translation,
                algorithmTranslations: translation.algorithmTranslations.map(
                  (algo, j) =>
                    j === algoIndex
                      ? {
                          ...algo,
                          problemCodeSnippets: [
                            ...algo.problemCodeSnippets,
                            {
                              codeSnippet: null,
                              active: true,
                              programmingLanguage:
                                metadata?.programmingLanguages?.[0]?.key,
                              description: null,
                            },
                          ],
                        }
                      : algo
                ),
              }
            : translation
        ),
      }));
    },
    [metadata, setProblemData]
  );

  const handleCodeSnippetChange = useCallback(
    (translationIndex, algoIndex, snippetIndex, field, value) => {
      setProblemData((prev) => ({
        ...prev,
        problemTranslations: prev.problemTranslations.map((translation, i) =>
          i === translationIndex
            ? {
                ...translation,
                algorithmTranslations: translation.algorithmTranslations.map(
                  (algo, j) =>
                    j === algoIndex
                      ? {
                          ...algo,
                          problemCodeSnippets: algo.problemCodeSnippets.map(
                            (snippet, k) =>
                              k === snippetIndex
                                ? { ...snippet, [field]: value }
                                : snippet
                          ),
                        }
                      : algo
                ),
              }
            : translation
        ),
      }));
    },
    [setProblemData]
  );

  const handleDeleteCodeSnippet = useCallback(
    (translationIndex, algoIndex, snippetIndex) => {
      setProblemData((prev) => ({
        ...prev,
        problemTranslations: prev.problemTranslations.map((translation, i) =>
          i === translationIndex
            ? {
                ...translation,
                algorithmTranslations: translation.algorithmTranslations.map(
                  (algo, j) =>
                    j === algoIndex
                      ? {
                          ...algo,
                          problemCodeSnippets: algo.problemCodeSnippets.filter(
                            (_, k) => k !== snippetIndex
                          ),
                        }
                      : algo
                ),
              }
            : translation
        ),
      }));
    },
    [setProblemData]
  );

  const handleCompanyChange = useCallback(
    (index, field, value) => {
      setProblemData((prev) => ({
        ...prev,
        problemCompanies: prev.problemCompanies.map((company, i) =>
          i === index ? { ...company, [field]: value } : company
        ),
      }));
    },
    [setProblemData]
  );

  const handleAddCompany = useCallback(
    (event) => {
      event.stopPropagation();
      setProblemData((prev) => ({
        ...prev,
        problemCompanies: [
          ...(Array.isArray(prev.problemCompanies)
            ? prev.problemCompanies
            : []),
          { companyName: null, imageUrl: null },
        ],
      }));
    },
    [setProblemData]
  );

  const handleDeleteCompany = useCallback(
    (index) => {
      setProblemData((prev) => ({
        ...prev,
        problemCompanies: prev.problemCompanies.filter((_, i) => i !== index),
      }));
    },
    [setProblemData]
  );

  const handleDeleteVideo = useCallback(
    (index) => {
      setProblemData((prev) => {
        if (
          index === undefined ||
          !prev.problemVideos ||
          !Array.isArray(prev.problemVideos)
        ) {
          console.warn(
            "Cannot delete: invalid index or empty/null problemVideos"
          );
          return { ...prev, problemVideos: [] };
        }
        const updatedVideos = prev.problemVideos.filter((_, i) => i !== index);
        return {
          ...prev,
          problemVideos: updatedVideos,
        };
      });
    },
    [setProblemData]
  );

  const handleVideoChange = useCallback(
    (index, field, value) => {
      if (field === "reorder" || field === "duplicate") {
        setProblemData((prev) => ({
          ...prev,
          problemVideos: value,
        }));
      } else {
        setProblemData((prev) => {
          if (!prev.problemVideos || prev.problemVideos.length === 0) {
            console.warn("Initializing problemVideos with new video");
            return {
              ...prev,
              problemVideos: [{ id: `${Date.now()}-0`, [field]: value }],
            };
          }
          const updatedVideos = prev.problemVideos.map((video, i) =>
            i === index ? { ...video, [field]: value } : video
          );
          return {
            ...prev,
            problemVideos: updatedVideos,
          };
        });
      }
    },
    [setProblemData]
  );

  const setProblemVideos = useCallback(
    (newVideos) => {
      setProblemData((prev) => ({
        ...prev,
        problemVideos:
          typeof newVideos === "function"
            ? newVideos(prev.problemVideos)
            : newVideos,
      }));
    },
    [setProblemData]
  );

  const handleAddVideo = useCallback(
    (event) => {
      setProblemData((prev) => ({
        ...prev,
        problemVideos: [
          ...(Array.isArray(prev.problemVideos) ? prev.problemVideos : []),
          {
            id: `${Date.now()}-${prev.problemVideos?.length || 0}`,
            videoLink: null,
            active: true,
            provider: metadata?.videoProviders?.[0]?.key || "YOUTUBE",
            videoTitle: null,
            videoDescription: null,
            programmingLanguage: metadata?.programmingLanguages?.[0]?.key,
            visibility: metadata?.visibilityTypes?.[0]?.key,
            previewAvailable: true,
          },
        ],
      }));
    },
    [metadata, setProblemData]
  );

  const handleTestChange = useCallback(
    (testIndex, section, index, field, value) => {
      setProblemData((prev) => {
        const newTests = [...prev.problemTests];
        if (section === "problemInputs") {
          const inputs = [...newTests[testIndex].problemInputs];
          if (field === "add") {
            inputs.push({
              variableName: null,
              value: null,
              variableType: "INTEGER",
            });
          } else if (field === "delete") {
            inputs.splice(index, 1);
          } else {
            inputs[index] = { ...inputs[index], [field]: value };
          }
          newTests[testIndex].problemInputs = inputs;
        } else if (section === "problemOutputs") {
          newTests[testIndex].problemOutputs = [
            {
              ...newTests[testIndex].problemOutputs[0],
              [field]: value,
            },
          ];
        }
        return { ...prev, problemTests: newTests };
      });
    },
    [setProblemData]
  );

  const handleAddTest = useCallback(
    (event) => {
      event.stopPropagation();
      setProblemData((prev) => ({
        ...prev,
        problemTests: [
          ...prev.problemTests,
          {
            testCase: null,
            problemInputs: [
              { variableName: null, value: null, variableType: "INTEGER" },
            ],
            problemOutputs: [
              { variableName: null, value: null, variableType: "INTEGER" },
            ],
          },
        ],
      }));
    },
    [setProblemData]
  );

  const handleDeleteTest = useCallback(
    (index) => {
      setProblemData((prev) => ({
        ...prev,
        problemTests: prev.problemTests.filter((_, i) => i !== index),
      }));
    },
    [setProblemData]
  );

  if ((isUpdate && !problemData.id) || !metadata)
    return <AddProblemSkeleton isUpdate={isUpdate} problemData={problemData} />;
  if (error) return <Typography color="error">Error: {error}</Typography>;

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", height: "100%", gap: 2 }}
    >
      <CardContent sx={{ p: 0 }}>
        <ProblemDetails
          expanded={expanded === "problemDetails"}
          onChange={handleAccordionChange("problemDetails")}
          problemData={problemData}
          handleChange={handleChange}
        />
        <ReactionsAccordion
          expanded={expanded === "reactions"}
          onChange={handleAccordionChange("reactions")}
          problemReactions={problemData.problemReactions}
          handleAddReaction={handleAddReaction}
          handleDeleteReaction={handleDeleteReaction}
          handleReactionChange={handleReactionChange}
          reactions={metadata.reactions}
          setProblemData={setProblemData}
        />
        <DifficultiesAccordion
          expanded={expanded === "difficultiesTags"}
          onChange={handleAccordionChange("difficultiesTags")}
          problemDifficulties={problemData.problemDifficulties}
          handleDifficultyChange={handleDifficultyChange}
          handleDeleteDifficulty={handleDeleteDifficulty}
          handleAddDifficulty={handleAddDifficulty}
          difficulties={metadata.difficulties}
          setProblemData={setProblemData}
        />
        <TagsAccordion
          expanded={expanded === "tags"}
          tagInput={tagInput}
          setTagInput={setTagInput}
          problemTags={problemData.problemTags}
          handleAddTag={handleAddTag}
          handleDeleteTag={handleDeleteTag}
          onChange={handleAccordionChange("tags")}
        />
        <CompaniesAccordion
          expanded={expanded === "companies"}
          onChange={handleAccordionChange("companies")}
          problemCompanies={problemData.problemCompanies}
          handleAddCompany={handleAddCompany}
          handleDeleteCompany={handleDeleteCompany}
          handleCompanyChange={handleCompanyChange}
          setProblemData={setProblemData}
        />
        <TranslationsAccordion
          expanded={expanded === "translations"}
          onChange={handleAccordionChange("translations")}
          problemTranslations={problemData.problemTranslations}
          handleAddTranslation={handleAddTranslation}
          handleTranslationChange={handleTranslationChange}
          handleDeleteTranslation={handleDeleteTranslation}
          handleAddAlgorithmTranslation={handleAddAlgorithmTranslation}
          handleAlgorithmTranslationChange={handleAlgorithmTranslationChange}
          handleDeleteAlgorithmTranslation={handleDeleteAlgorithmTranslation}
          handleAddCodeSnippet={handleAddCodeSnippet}
          handleCodeSnippetChange={handleCodeSnippetChange}
          handleDeleteCodeSnippet={handleDeleteCodeSnippet}
          programmingLanguages={metadata.programmingLanguages}
          languages={metadata.languages}
        />
        <VideosAccordion
          expanded={expanded === "videos"}
          onChange={handleAccordionChange("videos")}
          problemVideos={problemData.problemVideos}
          handleAddVideo={handleAddVideo}
          handleDeleteVideo={handleDeleteVideo}
          handleVideoChange={handleVideoChange}
          videoProviders={metadata.videoProviders}
          programmingLanguages={metadata.programmingLanguages}
          visibility={metadata.visibilityTypes}
          setProblemVideos={setProblemVideos}
        />
        <TestsAccordion
          expanded={expanded === "tests"}
          onChange={handleAccordionChange("tests")}
          problemTests={problemData.problemTests}
          handleAddTest={handleAddTest}
          handleDeleteTest={handleDeleteTest}
          handleTestChange={handleTestChange}
          variableTypes={metadata.variableTypes}
        />
      </CardContent>
    </Box>
  );
};

export default ProblemForm;
